import { get, post, put } from '../httpMethods';
import { ApiResponse } from '../../types/api';

export type DevLogLevel = 'log' | 'error' | 'warn' | 'debug' | 'verbose';

export interface DeveloperProcessInfo {
  uptimeSeconds: number;
  environment: string;
  appName: string;
  nodeVersion: string;
  platform: string;
  arch: string;
  pid: number;
  cpuCount: number;
  cpuUsagePercent: number;
  loadAverage: number[];
}

export interface DeveloperMemoryInfo {
  rss: number;
  heapTotal: number;
  heapUsed: number;
  external: number;
  arrayBuffers: number;
  system: {
    total: number;
    free: number;
    usedPercent: number;
  };
}

export interface DeveloperTableStat {
  name: string;
  rows: number;
}

export interface DeveloperDatabaseInfo {
  connected: boolean;
  latencyMs: number;
  size: string;
  sizeBytes: number;
  activeConnections: number;
  tableCount: number;
  totalRows: number;
  tables: DeveloperTableStat[];
  error?: string;
}

export interface DeveloperRequestStats {
  lifetime: {
    total: number;
    serverErrors: number;
    avgDurationMs: number;
  };
  recent: {
    windowSize: number;
    perMinute: number;
    avgDurationMs: number;
    p50DurationMs: number;
    p95DurationMs: number;
    maxDurationMs: number;
    buckets: {
      success: number;
      clientError: number;
      serverError: number;
    };
  };
}

export interface SpecimenBackfillSummary {
  status: string;
  candidates: number;
  queued: number;
  ranAt: string | null;
}

export interface DeveloperOverview {
  generatedAt: string;
  process: DeveloperProcessInfo;
  memory: DeveloperMemoryInfo;
  database: DeveloperDatabaseInfo;
  requests: DeveloperRequestStats;
  telemetry: {
    capturedLogs: number;
    capturedRequests: number;
  };
  specimenBackfill?: SpecimenBackfillSummary | null;
}

export interface DeveloperLogEntry {
  id: number;
  timestamp: string;
  level: DevLogLevel;
  context?: string;
  message: string;
  stack?: string;
}

export interface DeveloperLogResponse {
  generatedAt: string;
  entries: DeveloperLogEntry[];
}

export interface DeveloperIssue {
  id: string;
  type: 'error' | 'warning' | 'request';
  timestamp: string;
  title: string;
  detail?: string;
  context?: string;
  method?: string;
  url?: string;
  statusCode?: number;
  durationMs?: number;
}

export interface DeveloperIssueReport {
  generatedAt: string;
  summary: {
    total: number;
    errors: number;
    warnings: number;
    failedRequests: number;
    serverErrors: number;
    clientErrors: number;
    slowRequests: number;
  };
  items: DeveloperIssue[];
}

export interface DeveloperRequestRecord {
  id: number;
  timestamp: string;
  method: string;
  url: string;
  statusCode: number;
  durationMs: number;
  userId?: string;
  ip?: string;
  userAgent?: string;
}

export interface DeveloperRequestResponse {
  generatedAt: string;
  entries: DeveloperRequestRecord[];
}

export interface SuperAdmin {
  id: string;
  username: string;
  email: string;
  isActive: boolean;
  roles: string[];
  lastLoginAt?: string | null;
  createdAt: string;
}

export interface DeveloperAdminResponse {
  envAdmin: {
    username: string;
    email: string;
    passwordConfigured: boolean;
  };
  admins: SuperAdmin[];
}

export interface ResetDatabaseResult {
  truncatedTables: number;
  adminUsername: string;
  adminEmail: string;
  completedAt: string;
}

export interface CleanupModule {
  key: string;
  label: string;
  description: string;
  tables: string[];
  affectedTables: string[];
  tableCount: number;
  affectedTableCount: number;
  rowCount: number;
  affectedRowCount: number;
  touchesAdmin: boolean;
}

export interface CleanupModulesResponse {
  generatedAt: string;
  modules: CleanupModule[];
}

export interface CleanupResult {
  modules: string[];
  truncatedTables: number;
  affectedTables: string[];
  adminReseeded: boolean;
  permissionsRestored: boolean;
  completedAt: string;
}

export interface CacheNamespaceView {
  name: string;
  keys: number;
}

export interface CacheOverview {
  ready: boolean;
  totalKeys: number;
  defaultTtlSeconds: number;
  namespaces: CacheNamespaceView[];
  generatedAt: string;
}

export interface CacheInvalidateResult {
  scope: 'all' | 'namespaces';
  namespaces: string[];
  clearedKeys: number;
  completedAt: string;
}

export interface DeveloperLogParams {
  level?: DevLogLevel;
  search?: string;
  limit?: number;
}

/** A payment left on an invoice whose stay is no longer an inpatient stay. */
export interface StrayCollectionRow {
  paymentId: string;
  paymentNumber: string;
  paymentDate: string;
  amount: number;
  method: string;
  transactionId: string | null;
  invoiceId: string;
  invoiceNumber: string;
  invoiceStatus: string;
  invoiceTotal: number;
  invoicePaid: number;
  invoiceDue: number;
  invoiceCreatedAt: string;
  visitId: string;
  visitNumber: string;
  visitType: string;
  admissionStatus: string;
  admissionDeleted: boolean;
  admittedAt: string | null;
  admissionsCreatedAt: string | null;
  stayClosedAt: string | null;
  minutesAfterPayment: number | null;
  patientName: string;
  registrationNumber: string | null;
  receivedBy: string | null;
  clearable: boolean;
  blockedReason: string | null;
  recommendation: 'clear' | 'review' | 'keep';
  recommendationReason: string;
}

export interface FinanceRecommendation {
  action: 'clear' | 'review' | 'none';
  paymentIds: string[];
  amount: number;
  headline: string;
  detail: string;
}

export interface StrayLedgerCheck {
  ledger: 'pharmacy' | 'diagnostic';
  label: string;
  records: number;
  amount: number;
  note: string;
}

export interface FinanceIntegrityScan {
  generatedAt: string;
  payments: StrayCollectionRow[];
  totals: {
    payments: number;
    amount: number;
    clearablePayments: number;
    clearableAmount: number;
    invoicesAffected: number;
    invoicesToCancel: number;
    recommendedPayments: number;
    recommendedAmount: number;
  };
  recommendation: FinanceRecommendation;
  otherLedgers: StrayLedgerCheck[];
}

export interface FinanceIntegrityClearResult {
  deletedPayments: number;
  clearedAmount: number;
  cancelledInvoices: string[];
  skipped: string[];
  cacheKeysCleared: number;
  completedAt: string;
}

export interface AiProviderView {
  configured: boolean;
  provider: string;
  baseUrl: string;
  model: string;
  enabled: boolean;
  apiKeyHint: string | null;
  lastCheckedAt: string | null;
  lastStatus: string | null;
}

export interface AiFinanceReview {
  configured: true;
  provider: string;
  model: string;
  assessment: string;
  confidence: 'high' | 'medium' | 'low';
  summaryForClient: string;
  recommendations: Array<{
    paymentId: string;
    action: 'clear' | 'review' | 'keep';
    reason: string;
  }>;
  repairPlan: string[];
  warnings: string[];
  agents: Array<{
    role: 'auditor' | 'accountant' | 'fraud' | 'chair';
    name: string;
    verdict: string;
    note: string;
  }>;
  generatedAt: string;
}

export interface AiModelOption {
  id: string;
  name: string;
  description: string | null;
  contextWindow: number | null;
  free: boolean;
}

export interface AiModelCatalog {
  source: 'remote' | 'fallback';
  models: AiModelOption[];
  warning: string | null;
}

export interface AiRepairAnswer {
  provider: string;
  model: string;
  answer: string;
  generatedAt: string;
}

export type ReconciliationSeverity = 'ok' | 'warning' | 'critical';

export interface ReconciliationSample {
  id: string;
  reference: string;
  amount: number;
  issue: string;
}

export interface ReconciliationCheck {
  key: string;
  label: string;
  description: string;
  billing: number | null;
  cashbook: number | null;
  reports: number | null;
  labels: { billing: string; cashbook: string; reports: string };
  deltaReports: number;
  deltaCashbook: number | null;
  aligned: boolean;
  severity: ReconciliationSeverity;
  note: string;
}

export interface ReconciliationRepair {
  supported: boolean;
  label: string;
  description: string;
}

export interface ReconciliationFinding {
  key: string;
  label: string;
  description: string;
  severity: 'warning' | 'critical';
  count: number;
  hasMore: boolean;
  amount: number;
  samples: ReconciliationSample[];
  repair: ReconciliationRepair;
}

export interface LedgerReconciliation {
  generatedAt: string;
  summary: {
    checks: number;
    aligned: number;
    misaligned: number;
    critical: number;
    totalAbsoluteDelta: number;
  };
  checks: ReconciliationCheck[];
  findings: ReconciliationFinding[];
  totals: {
    billing: {
      invoicePayments: number;
      invoiceCashPayments: number;
      invoiceRevenue: number;
      totalCollections: number;
      cashCollections: number;
    };
    cashbook: { cashIn: number; cashOut: number; balance: number };
    journal: { cashBalance: number; unbalancedEntries: number; revenue: number };
    reports: {
      collections: number;
      invoiceRevenue: number;
      totalRevenue: number;
      cashbookBalance: number;
    };
  };
}

export interface LedgerBackfillSourceResult {
  sourceType: string;
  label: string;
  scanned: number;
  posted: number;
  replaced: number;
  skipped: number;
  errors: number;
}

export interface LedgerCashbookMirrorResult {
  mirrored: number;
  legacyRemoved: number;
  skipped: number;
  errors: number;
}

export interface LedgerBackfillResult {
  period: { dateFrom: string | null; dateTo: string | null };
  dryRun: boolean;
  cashbookMode: 'preserve' | 'mirror' | 'takeover';
  generatedAt: string;
  totals: Omit<LedgerBackfillSourceResult, 'sourceType' | 'label'>;
  sources: LedgerBackfillSourceResult[];
  cashbook: LedgerCashbookMirrorResult;
}

export interface LedgerRepairFindingResult {
  key: string;
  label: string;
  action: string;
  supported: boolean;
  affected: number;
  amount: number;
  changed: number;
  skipped: number;
  errors: number;
  note: string;
}

export interface LedgerRepairResult {
  generatedAt: string;
  dryRun: boolean;
  findings: LedgerRepairFindingResult[];
  totals: {
    affected: number;
    amount: number;
    changed: number;
    skipped: number;
    errors: number;
  };
  cacheKeysCleared: number;
}

export interface AiReviewResponse {
  review: AiFinanceReview | null;
  configured: boolean;
}

/** Public website mode controlled from the developer console. */
export type FrontendMode = 'live' | 'development';

export interface FrontendModeResponse {
  mode: FrontendMode;
}

export const developerService = {
  getOverview: (): Promise<ApiResponse<DeveloperOverview>> =>
    get<DeveloperOverview>('/developer/overview'),

  getLogs: (params: DeveloperLogParams = {}): Promise<ApiResponse<DeveloperLogResponse>> =>
    get<DeveloperLogResponse>('/developer/logs', params),

  getIssues: (limit = 100): Promise<ApiResponse<DeveloperIssueReport>> =>
    get<DeveloperIssueReport>('/developer/issues', { limit }),

  getRequests: (limit = 100): Promise<ApiResponse<DeveloperRequestResponse>> =>
    get<DeveloperRequestResponse>('/developer/requests', { limit }),

  listAdmins: (): Promise<ApiResponse<DeveloperAdminResponse>> =>
    get<DeveloperAdminResponse>('/developer/admins'),

  getFrontendMode: (): Promise<ApiResponse<FrontendModeResponse>> =>
    get<FrontendModeResponse>('/developer/frontend-mode'),

  setFrontendMode: (mode: FrontendMode): Promise<ApiResponse<FrontendModeResponse>> =>
    put<FrontendModeResponse>('/developer/frontend-mode', { mode }),

  getCleanupModules: (): Promise<ApiResponse<CleanupModulesResponse>> =>
    get<CleanupModulesResponse>('/developer/modules'),

  getCacheOverview: (): Promise<ApiResponse<CacheOverview>> =>
    get<CacheOverview>('/developer/cache'),

  invalidateCache: (payload: {
    all?: boolean;
    namespaces?: string[];
    confirmation?: string;
  }): Promise<ApiResponse<CacheInvalidateResult>> =>
    post<CacheInvalidateResult>('/developer/cache/invalidate', payload),

  cleanModules: (
    modules: string[],
    confirmation: string,
  ): Promise<ApiResponse<CleanupResult>> =>
    post<CleanupResult>('/developer/cleanup', { modules, confirmation }),

  upsertAdmin: (data: {
    username: string;
    password: string;
    email?: string;
  }): Promise<ApiResponse<SuperAdmin>> => post<SuperAdmin>('/developer/admins', data),

  resetDatabase: (confirmation: string): Promise<ApiResponse<ResetDatabaseResult>> =>
    post<ResetDatabaseResult>('/developer/reset', { confirmation }),

  scanFinanceIntegrity: (): Promise<ApiResponse<FinanceIntegrityScan>> =>
    get<FinanceIntegrityScan>('/developer/finance-integrity'),

  getLedgerReconciliation: (): Promise<ApiResponse<LedgerReconciliation>> =>
    get<LedgerReconciliation>('/developer/ledger-reconciliation'),

  backfillLedger: (payload: {
    dryRun?: boolean;
    cashbookMode?: 'preserve' | 'mirror' | 'takeover';
    dateFrom?: string;
    dateTo?: string;
    confirmation?: string;
  }): Promise<ApiResponse<LedgerBackfillResult>> =>
    post<LedgerBackfillResult>('/developer/ledger-reconciliation/backfill', payload),

  repairLedger: (payload: {
    findingKeys: string[];
    dryRun?: boolean;
    confirmation?: string;
  }): Promise<ApiResponse<LedgerRepairResult>> =>
    post<LedgerRepairResult>('/developer/ledger-reconciliation/repair', payload),

  clearFalseData: (
    paymentIds: string[],
    confirmation: string,
  ): Promise<ApiResponse<FinanceIntegrityClearResult>> =>
    post<FinanceIntegrityClearResult>('/developer/finance-integrity/clear', {
      paymentIds,
      confirmation,
    }),

  getAiConfig: (): Promise<ApiResponse<AiProviderView>> =>
    get<AiProviderView>('/developer/ai/config'),

  saveAiConfig: (payload: {
    provider: string;
    baseUrl: string;
    model: string;
    apiKey?: string;
    enabled?: boolean;
  }): Promise<ApiResponse<AiProviderView>> => put<AiProviderView>('/developer/ai/config', payload),

  testAiProvider: (): Promise<
    ApiResponse<{ ok: boolean; message: string; latencyMs: number; reply: string }>
  > => post('/developer/ai/test', {}),

  listAiModels: (payload: {
    provider: string;
    baseUrl: string;
    apiKey?: string;
  }): Promise<ApiResponse<AiModelCatalog>> =>
    post<AiModelCatalog>('/developer/ai/models', payload),

  askAi: (question: string): Promise<ApiResponse<AiRepairAnswer>> =>
    post<AiRepairAnswer>('/developer/ai/ask', { question }),

  reviewFinanceWithAi: (): Promise<ApiResponse<AiReviewResponse>> =>
    post<AiReviewResponse>('/developer/ai/finance-review', {}),
};
